'use client'

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FaqService } from '@/lib/services'
import { FaqItem } from '@/lib/services/types'
import DOMPurify from 'isomorphic-dompurify'
import { trackClarityEvent } from '@/lib/clarity'

export const FaqSection = () => {
    // State for expanded accordion items
    // Using a map to track open/closed state of each item by ID
    const [openItems, setOpenItems] = useState<Record<number, boolean>>({})

    const toggleItem = (faq: FaqItem) => {
        const isOpening = !openItems[faq.id];
        
        if (isOpening) {
            // Send to Clarity only when the user expands the accordion
            trackClarityEvent(`faq_abertura: ${faq.pergunta.substring(0, 60)}...`);
        }

        setOpenItems((prev) => ({
            ...prev,
            [faq.id]: isOpening,
        }))
    }

    // Fetch FAQs from API
    const { data: faqs, isLoading, error } = useQuery({
        queryKey: ['faqs'],
        queryFn: () => FaqService.getFaqs(),
        staleTime: 1000 * 60 * 5, // 5 minutes cache
    })

    // Loading state
    if (isLoading) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="bg-white shadow-xl rounded-2xl p-8 md:p-12 lg:p-16 animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-10"></div>
                    <div className="space-y-6">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                </div>
            </div>
        )
    }

    // Error state
    if (error) {
        console.error('Error fetching FAQs:', error)
        return null // Hide section on error or show a friendly message if preferred
    }

    // Empty state
    if (!faqs || faqs.length === 0) {
        return null
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="bg-white shadow-2xl rounded-2xl p-8 md:p-12 lg:p-16">
                <h2 className="text-3xl font-medium text-[#0B2110] text-center mb-10">Perguntas Frequentes</h2>

                <div className="space-y-4">
                    {faqs.map((faq: FaqItem) => {
                        const isOpen = openItems[faq.id] || false

                        return (
                            <div key={faq.id} className="border-b border-[#D7DEF0] pb-6 last:border-0 last:pb-0">
                                <div
                                    className="flex justify-between items-center py-4 cursor-pointer group"
                                    onClick={() => toggleItem(faq)}
                                >
                                    <h3 className="text-lg font-medium text-[#0B2110] group-hover:text-[#5AE4A7] transition-colors">
                                        {faq.pergunta}
                                    </h3>
                                    <button
                                        className={`text-xl transition-transform duration-300 ${isOpen ? 'text-[#5AE4A7] rotate-45' : 'text-[#1A0F91]'}`}
                                    >
                                        +
                                    </button>
                                </div>

                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100 mb-4' : 'max-h-0 opacity-0'}`}
                                >
                                    <div
                                        className="text-[#627065] text-sm leading-relaxed prose prose-green max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(faq.resposta, {
                                                ADD_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td'],
                                                ADD_ATTR: ['style', 'class']
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
